import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { STEPS } from './steps';
import { getClinicProfile, updateClinicProfile } from '../../Services/APIs/authentication';
import { getClinicSubscriptionStatus } from '../../Services/APIs/appDetails';
import { setClinicDetails } from '../../Utils/clinicAuth';
import { getFlowDecision } from '../../Utils/clinicFunnel';
import Welcome from './Welcome';
import Practise from './Practise';
import Share from './Share';
import Done from './Done';

const BANNER_CLASSES = "flex flex-wrap items-center justify-center gap-3 bg-red-50 border-b border-red-200 px-4 py-3 text-sm text-red-700";

const ACTIVATION_FAILED = "We couldn't finish setting up your account. Please reload and try again.";

const STEP_COMPONENTS = {
    welcome: Welcome,
    practise: Practise,
    share: Share,
    done: Done,
};

const Onboarding = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [stepIndex, setStepIndex] = useState(0);
    const [activationError, setActivationError] = useState('');
    const [isLeaving, setIsLeaving] = useState(false);
    const { statusError, retryStatus } = useOutletContext();

    const [activationDidNotHold] = useState(() => location.state?.redirectedFrom === '/dashboard');

    useEffect(() => {
        if (!activationDidNotHold) return;
        navigate(location.pathname, { replace: true, state: null });
    }, [activationDidNotHold, navigate, location.pathname]);

    useEffect(() => {
        getClinicProfile()
            .then((response) => setClinicDetails(response.data?.data))
            .catch((error) => console.error('Error fetching clinic details:', error));
    }, []);

    const goBack = () => setStepIndex((prev) => Math.max(prev - 1, 0));

    const leaveOnboarding = async (route = '/dashboard') => {
        if (isLeaving) return;
        setIsLeaving(true);
        setActivationError('');
        try {
            const response = await updateClinicProfile({ status: 'active' });
            const activatedStatus = response?.data?.data?.status;

            if (activatedStatus && activatedStatus !== 'active') {
                setActivationError(ACTIVATION_FAILED);
                setIsLeaving(false);
                return;
            }

            navigate(route, {
                replace: true,
                state: activatedStatus === 'active' ? { funnelResolved: true } : undefined,
            });
        } catch (error) {
            console.error('Error activating clinic profile:', error);
            const message = error?.response?.data?.message || error?.data?.message || ACTIVATION_FAILED;
            try {
                const response = await getClinicSubscriptionStatus();
                const nextRoute = getFlowDecision(response?.data?.data.status, response?.data?.data.subscription_status);
                if (nextRoute === '/onboarding') setActivationError(message);
                else navigate(nextRoute, { replace: true });
            } catch (statusFetchError) {
                console.error('Error fetching subscription status:', statusFetchError);
                setActivationError(message);
            }
        }
        setIsLeaving(false);
    };

    const goNext = () => {
        if (stepIndex < STEPS.length - 1) {
            setStepIndex((prev) => prev + 1);
            return;
        }
        leaveOnboarding('/dashboard');
    };

    const StepComponent = STEP_COMPONENTS[STEPS[stepIndex].key];

    return (
        <>
            {(statusError || activationError || activationDidNotHold) && (
            <div className="fixed top-0 inset-x-0 z-50">
                {statusError && (
                    <div className={BANNER_CLASSES}>
                        <span>{statusError} You can continue setting up, but this may not be the right page for your account.</span>
                        <button type="button" onClick={retryStatus} className="underline underline-offset-2">
                            Retry
                        </button>
                    </div>
                )}
                {activationDidNotHold && !activationError && (
                    <div className={BANNER_CLASSES}>
                        <span>{ACTIVATION_FAILED}</span>
                    </div>
                )}
                {activationError && (
                    <div className={BANNER_CLASSES}>
                        <span>{activationError}</span>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            className="underline underline-offset-2"
                        >
                            Reload
                        </button>
                    </div>
                )}
            </div>
            )}
            <StepComponent onBack={goBack} onNext={goNext} onLeave={leaveOnboarding} />
        </>
    );
};

export default Onboarding;
